import { useEffect, useState, useCallback, useRef } from "react";
import { ListPaymentMethods } from "../../Api/Stripe";

export interface UserPaymentMethod {
    id: string;
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    is_default: boolean;
}

function normalizePm(raw: any): UserPaymentMethod {
    return {
        id: raw.id,
        brand: raw.brand,
        last4: raw.last4,
        exp_month: raw.exp_month ?? raw.expMonth,
        exp_year: raw.exp_year ?? raw.expYear,
        is_default: (raw.is_default ?? raw.isDefault) === true,
    };
}

// NEW: reloadKey param lets you re-trigger the effect from outside
export default function useFetchUserPaymentMethods(
    userId: string | null | undefined,
    getValidAccessToken: () => Promise<string | null>,
    reloadKey?: string | number | boolean
) {
    const [methods, setMethods] = useState<UserPaymentMethod[]>([]);
    const [defaultPM, setDefaultPM] = useState<UserPaymentMethod | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const getTokenRef = useRef(getValidAccessToken);
    getTokenRef.current = getValidAccessToken;

    const fetchNow = useCallback(async (uid: string, ac: AbortController) => {
        setError("");
        setLoading(true);

        const accessToken = await getTokenRef.current();
        if (!accessToken) {
            setError("Your session has expired. Please log in and try again.");
            setMethods([]);
            setDefaultPM(null);
            setLoading(false);
            return;
        }

        const res = await ListPaymentMethods(uid, accessToken, ac);

        // Your axios helper sometimes returns AxiosError instead of throwing
        if ((res as any)?.isAxiosError) {
            const msg =
                (res as any)?.message || "Failed to load payment methods.";
            setError(msg);
            setMethods([]);
            setDefaultPM(null);
            setLoading(false);
            return;
        }

        const arr = Array.isArray(res) ? res : [];
        const normalized = arr.map(normalizePm);
        setMethods(normalized);

        if (normalized.length === 0) {
            setDefaultPM(null);
        } else {
            // prefer the one flagged default; fallback to first
            const d = normalized.find(pm => pm.is_default) ?? normalized[0];
            setDefaultPM(d);
        }

        setLoading(false);
    }, []);

    useEffect(() => {
        let isCancelled = false;
        const ac = new AbortController();

        setMethods([]);
        setDefaultPM(null);
        setError("");

        if (!userId || !userId.trim()) {
            return () => {
                isCancelled = true;
                ac.abort();
            };
        }

        (async () => {
            try {
                await fetchNow(userId, ac);
            } catch (e: any) {
                if (!isCancelled) {
                    setError(e?.message || "Failed to load payment methods.");
                    setMethods([]);
                    setDefaultPM(null);
                    setLoading(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
            ac.abort();
        };
        // 🔑 reloadKey added here so changing it retriggers fetch
    }, [userId, reloadKey, fetchNow]);

    const refresh = useCallback(() => {
        if (!userId || !userId.trim()) return;
        const ac = new AbortController();
        fetchNow(userId, ac);
        return () => ac.abort();
    }, [userId, fetchNow]);

    return { methods, defaultPM, loading, error, refresh };
}
